import { z } from 'zod'
import { gameTicketName } from './commerce'
import { emptyText } from './format'

export const channelKinds = ['douyin', 'meituan'] as const
export const channelRedemptionStatuses = [
  'pending',
  'succeeded',
  'failed',
] as const
export const channelFailReasons = [
  'invalid_code',
  'offer_unmapped',
  'template_unusable',
  'multi_use_unsupported',
  'credential_expired',
  'platform_error',
] as const

export type ChannelKind = (typeof channelKinds)[number]
export type ChannelRedemptionStatus = (typeof channelRedemptionStatuses)[number]
export type ChannelFailReason = (typeof channelFailReasons)[number]

export const channelRateWindowMs = 10 * 60 * 1000
export const channelRateWindowLimit = 10
export const channelBackoffStartCount = 3
export const channelBackoffMaxSeconds = 5 * 60

export function channelBackoffSeconds(consecutiveInvalid: number): number {
  if (consecutiveInvalid < channelBackoffStartCount) return 0
  return Math.min(2 ** (consecutiveInvalid - 2), channelBackoffMaxSeconds)
}

export const channelRedemptionSchema = z.object({
  channel: z.enum(channelKinds, { error: '请选择核销渠道' }),
  code: z
    .string()
    .trim()
    .min(1, '请输入券码')
    .max(64, '券码过长')
    .transform((value) => value.replaceAll(/\s+/g, '')),
  storeId: z.string().uuid('门店参数不合法').optional(),
  seatToken: z.string().min(12, '座位凭证不合法').optional(),
})

export const channelRedemptionLookupSchema = z.object({
  seatToken: z.string().min(12, '座位凭证不合法').optional(),
})

const emptyToUndefined = (value: unknown) =>
  value === '' || value === undefined ? undefined : value

const emptyToNull = (value: unknown) =>
  value === '' || value === undefined ? null : value

export const meituanChannelFieldsSchema = z.object({
  externalShopId: z.string().trim().min(1, '请填写美团门店编号'),
  appAuthToken: z.preprocess(emptyToUndefined, z.string().trim().optional()),
  authExpiresAt: z.string().trim().min(1, '请填写美团授权到期时间'),
})

export const douyinChannelFieldsSchema = z.object({
  externalShopId: z.string().trim().min(1, '请填写来客门店 POI'),
  accountId: z.preprocess(emptyToNull, z.string().trim().nullable().optional()),
})

export const meituanChannelBindingSchema = meituanChannelFieldsSchema.extend({
  storeId: z.string().uuid('请选择门店'),
  channel: z.literal('meituan'),
})

export const douyinChannelBindingSchema = douyinChannelFieldsSchema.extend({
  storeId: z.string().uuid('请选择门店'),
  channel: z.literal('douyin'),
})

export const storeChannelBindingSchema = z.discriminatedUnion('channel', [
  meituanChannelBindingSchema,
  douyinChannelBindingSchema,
])

export type MeituanChannelFields = z.output<typeof meituanChannelFieldsSchema>
export type DouyinChannelFields = z.output<typeof douyinChannelFieldsSchema>

export const channelOfferGrantCountMax = 99

export interface ChannelOfferGrant {
  voucherId: string | null
  count: number
}

export const channelOfferGrantSchema = z.object({
  voucherId: z.preprocess(
    (value) => (value === '' || value === undefined ? null : value),
    z.string().uuid('请选择票券').nullable(),
  ),
  count: z.coerce
    .number({ error: '请输入张数' })
    .int('张数必须是整数')
    .min(1, '张数必须大于 0')
    .max(channelOfferGrantCountMax, `最多 ${channelOfferGrantCountMax} 张`),
})

export const channelOfferGrantsSchema = z
  .array(channelOfferGrantSchema)
  .min(1, '请至少添加一张兑出券')
  .superRefine((grants, context) => {
    const seen = new Set<string>()
    for (const [index, grant] of grants.entries()) {
      const key = grant.voucherId ?? 'ticket'
      if (seen.has(key)) {
        context.addIssue({
          code: 'custom',
          path: [index, 'voucherId'],
          message: '同一种券只需填写一行',
        })
      }
      seen.add(key)
    }
  })

export const channelOfferMappingSchema = z.object({
  bindingId: z.string().uuid('请选择门店渠道绑定'),
  externalDealId: z.string().trim().min(1, '请填写套餐编号'),
  externalDealTitle: z.string().trim().min(1, '请填写套餐备注名').max(80),
  grants: channelOfferGrantsSchema,
  isEnabled: z.boolean().optional().default(true),
})

export function defaultChannelOfferGrant(): ChannelOfferGrant {
  return { voucherId: null, count: 1 }
}

export function parseChannelOfferGrants(value: unknown): ChannelOfferGrant[] {
  const parsed = channelOfferGrantsSchema.safeParse(value)
  return parsed.success ? parsed.data : []
}

export function channelOfferGrantCount(grants: ChannelOfferGrant[]): number {
  return grants.reduce((sum, grant) => sum + grant.count, 0)
}

export function channelOfferGrantTypeOptions(
  vouchers: Array<{ id: string; name: string }>,
): Array<{ value: string; label: string }> {
  return [
    { value: 'ticket', label: gameTicketName },
    ...vouchers.map((item) => ({ value: item.id, label: item.name })),
  ]
}

export function channelOfferGrantValue(grant: ChannelOfferGrant): string {
  return grant.voucherId ?? 'ticket'
}

export function channelOfferGrantFromValue(
  value: string,
  grant: ChannelOfferGrant,
): ChannelOfferGrant {
  if (value === 'ticket') return { voucherId: null, count: grant.count }
  return { voucherId: value, count: grant.count }
}

export function formatChannelGrant(
  grant: ChannelOfferGrant,
  vouchers: Array<{ id: string; name: string }> = [],
): string {
  const name = grant.voucherId
    ? (vouchers.find((item) => item.id === grant.voucherId)?.name ?? '票券')
    : gameTicketName
  return `${name} ×${grant.count}`
}

export function formatChannelGrants(
  grants: ChannelOfferGrant[],
  vouchers: Array<{ id: string; name: string }> = [],
): string {
  if (grants.length === 0) return emptyText
  return grants.map((grant) => formatChannelGrant(grant, vouchers)).join('、')
}

export function formatIssuedChannelVouchers(
  vouchers: Array<{ name: string }>,
): string {
  if (vouchers.length === 0) return emptyText
  const counts = new Map<string, number>()
  for (const voucher of vouchers)
    counts.set(voucher.name, (counts.get(voucher.name) ?? 0) + 1)
  return [...counts]
    .map(([name, count]) => `${name} ×${count}`)
    .join('、')
}

export interface ChannelBindingView {
  id: string
  storeId: string
  storeName: string
  channel: ChannelKind
  channelLabel: string
  externalShopId: string
  accountId: string | null
  hasAppAuthToken: boolean
  authExpiresAt: string | null
  authInvalidatedAt: string | null
  isExpired: boolean
  isInvalidated: boolean
}

export interface ChannelOfferMappingView {
  id: string
  bindingId: string
  storeId: string
  channel: ChannelKind
  channelLabel: string
  externalDealId: string
  externalDealTitle: string
  grants: ChannelOfferGrant[]
  grantsLabel: string
  isEnabled: boolean
}

export interface ChannelRedemptionListItem {
  id: string
  createdAt: string
  createdAtLabel: string
  channel: ChannelKind
  channelLabel: string
  storeId: string
  storeName: string
  memberId: string
  memberNo: string
  nickname: string
  avatarUrl: string | null
  externalDealId: string | null
  externalDealTitle: string | null
  status: ChannelRedemptionStatus
  statusLabel: string
  failReason: ChannelFailReason | null
  failReasonLabel: string
  ticketName: string
  externalReceiptId: string | null
  canSync: boolean
}

export interface ChannelRedemptionSummary {
  succeededCount: number
  issuedCount: number
  usedCount: number
  usedAmount: number
}
